import { Menu, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/features/notifications';
import { useSession } from '@/stores/session';
import { LanguageToggle } from './LanguageToggle';
import { OsvitlyLogo } from './OsvitlyLogo';
import { SchoolSwitcher } from './SchoolSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';

interface HeaderProps {
  /** Opens the mobile navigation drawer. */
  onMenuClick: () => void;
  /** Whole-sidebar collapse toggle (desktop). */
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}

export function Header({ onMenuClick, sidebarCollapsed, onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation('common');
  const activeSlug = useSession(s => s.activeSchoolSlug);
  const memberships = useSession(s => s.memberships);
  const active = memberships.find(m => m.school.slug === activeSlug)?.school;

  return (
    <header className="bg-sidebar text-sidebar-foreground border-sidebar-border sticky top-0 z-30 flex h-14 items-center gap-2 border-b px-3 md:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        aria-label={t('openMenu')}
      >
        <Menu />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="hidden lg:inline-flex"
        onClick={onToggleSidebar}
        aria-pressed={sidebarCollapsed}
        aria-label={sidebarCollapsed ? t('showSidebar') : t('hideSidebar')}
        title={sidebarCollapsed ? t('showSidebar') : t('hideSidebar')}
      >
        <PanelLeft />
      </Button>

      <Link to="/" className="flex items-center gap-2 font-semibold">
        {active?.logo ? (
          <>
            <img
              src={active.logo}
              alt={`${active.name} logo`}
              className="size-7 rounded-md object-cover"
            />
            <span className="hidden sm:inline">{active.name}</span>
          </>
        ) : (
          <OsvitlyLogo />
        )}
      </Link>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        <SchoolSwitcher />
        <NotificationBell />
        <LanguageToggle />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
